import { StatsDataEntityInterface } from '../data/entity.interface';
import { StatsDataServiceInterface } from '../../../service/stats-data/service.interface';
import { StatsStoreInterface } from '../store.interface';
import { StatsData } from '../data';

export class StatsEmitterEntityStrategy {
  /**
   * List of emitted event hash
   */
  protected emitted: string[] = [];

  /**
   *
   * @param statsDataService
   */
  constructor(private statsDataService: StatsDataServiceInterface) {}

  /**
   * Reset list of emitted event hash
   */
  public reset(): void {
    this.emitted = [];
  }

  /**
   * Returns a promise of an StatsDataEntityInterface that can be emitted in an event @eventName
   * Can prevent a same event @eventName for the @entityName identified by @entityId to be emitted twice.
   *
   * @param entityName
   * @param eventName
   * @param entityId
   * @param emitOnce
   * @param eventHash - Optional event hash, if empty a basic hash will be generated using this.getEventHash()
   */
  public canEmit(
    entityName: string,
    eventName: string,
    entityId: number,
    emitOnce?: boolean,
    eventHash?: string
  ): Promise<StatsDataEntityInterface> {
    return new Promise((resolve, reject) => {
      // Unique event hash
      if (!eventHash) {
        eventHash = this.getEventHash(entityName, eventName, entityId);
      }

      // Emit once required but event already emitted
      if (emitOnce === true && this.isEmitted(eventHash)) {
        reject();
        return;
      }

      // Set event hash as emitted
      this.setEmitted(eventHash);

      // Load data & emit event
      this.getStore(entityName).load(entityId).then(resolve).catch(reject);
    });
  }

  /**
   * Returns an event hash for @entityName, @eventName, @entityId combination
   *
   * @param entityName
   * @param eventName
   * @param entityId
   */
  public getEventHash(entityName: string, eventName: string, entityId: number): string {
    return [entityName, eventName, String(entityId)].join('-');
  }

  /**
   * Returns true if the @eventHash has already been emitted
   *
   * @param eventHash
   */
  public isEmitted(eventHash: string): boolean {
    return this.emitted.indexOf(eventHash) !== -1;
  }

  /**
   * Sets the @eventHash as emitted
   *
   * @param eventHash
   */
  public setEmitted(eventHash: string): void {
    this.emitted.push(eventHash);
  }

  /**
   * Returns a stats store for the entity @entityName
   *
   * @param entityName
   */
  protected getStore(entityName: string): StatsStoreInterface {
    // Property store
    if (entityName === StatsData.PROPERTY) {
      return this.statsDataService.getPropertyStore();
    }

    // Agent store
    if (entityName === StatsData.AGENT) {
      return this.statsDataService.getAgentStore();
    }

    // Broker store
    if (entityName === StatsData.BROKER) {
      return this.statsDataService.getBrokerStore();
    }

    // User store
    if (entityName === StatsData.USER) {
      return this.statsDataService.getUserStore();
    }

    // Store not supported
    throw new Error('Stats store "' + entityName + '" is not supported by StatsEmitter');
  }
}
